import type { ServiceItem } from '@/cms/fields/types/checkout-page'

import { Banner } from '@sunwebgroup/uds-ui/banner'
import { Icon } from '@sunwebgroup/uds-ui/icon'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import { cn } from '@sunwebgroup/uds-ui/utils'
import * as React from 'react'
import { useIntl } from '@/intl'
import { CheckoutSection, Description } from '@/scopes/checkout/components/CheckoutSection'
import { getIconForService } from '@/scopes/checkout/components/ServiceIcon'
import { CarOption } from '@/scopes/checkout/components/services/CarRentalFilteringService/CarOption'
import { CarsListView } from '@/scopes/checkout/components/services/CarRentalFilteringService/CarsListView'
import { ExtraCarsView } from '@/scopes/checkout/components/services/CarRentalFilteringService/ExtraCarsView'
import {
  getFirstCarIncluded,
  getThePriceDifferenceToUpgradeIncludedCar,
  isAnyCarOptionSelectedAndIncluded,
  isAnyCarOptionSelectedAndIncludedAndUpgradeable,
} from '@/scopes/checkout/components/services/CarRentalFilteringService/selectors'
import {
  carIsBeingSelected,
  carIsBeingUpgraded,
  carToBeSelected,
  useCarRentalFilteringServiceStore,
} from '@/scopes/checkout/components/services/CarRentalFilteringService/store'
import { UpgradeCarsListView } from '@/scopes/checkout/components/services/CarRentalFilteringService/UpgradeCarsListView'
import { useSendAncillaryAvailable } from '@/scopes/checkout/domain/analytics/useSendAncillaryAvailable'
import { useService } from '@/scopes/checkout/domain/hooks'
import { ServiceType } from '@/scopes/checkout/domain/types'
import { useGetAllCarsQuery } from '@/scopes/checkout/filterableCars/domain/hooks'
import {
  buildUniqOptionCarId,
} from '@/scopes/checkout/infrastructure/api/service-mappers/carRentalFilteringServiceMapper'
import styles from './CarRentalFilteringService.module.css'

export function CarRentalFilteringService({ item }: { item: ServiceItem }) {
  const { componentTitle, componentDescription, popupButtonAriaLabel, popupTitle, popupDescription, popupReadMoreLabel } = item
  const intl = useIntl()
  const { service } = useService(ServiceType.CarRentalFiltering)

  useSendAncillaryAvailable(service, 'selectCarRental')
  const isCarBeingUpgraded = useCarRentalFilteringServiceStore(carIsBeingUpgraded)
  const isCarBeingSelected = useCarRentalFilteringServiceStore(carIsBeingSelected)
  const selectingCar = useCarRentalFilteringServiceStore(carToBeSelected)

  if (!service)
    return null
  const { usps, carLists, options } = service
  const selectingCarOption = selectingCar ? options?.[selectingCar] : null

  const isAnyCarIncluded = isAnyCarOptionSelectedAndIncluded(Object.values(options || {}), Object.values(service.carLists || {}).map(({ selectedId }) => selectedId ?? ''))

  return (
    <CheckoutSection
      title={componentTitle ?? intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_title' }) ?? ''}
      description={componentDescription ?? intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_description' })}
      glyph={getIconForService(ServiceType.CarRentalFiltering)}
      moreInformationTitle={popupTitle}
      moreInformationDescription={popupDescription}
      moreInformationButtonAriaLabel={popupButtonAriaLabel}
      moreInformationReadMoreLabel={popupReadMoreLabel}
      id={ServiceType.CarRentalFiltering}
    >
      <div className={cn(styles.mainContent)}>
        <Description>
          {usps?.map(({ link, text }) => (
            <div className={cn(styles.itemWrapper)} key={text}>
              <Icon className={cn(styles.iconWrapperUsp)} glyph="CheckMarkSmall" aria-hidden />
              <div className={cn(styles.textWrapper)}>
                <Typography variant="body-m-regular-tight">
                  {link && <a href={link} target="_blank" rel="noopener noreferrer">{text}</a>}
                  {!link && text}
                </Typography>
              </div>
            </div>
          ))}
        </Description>

        <WarningMessages />

        {/* WHEN CAR IS ALREADY SELECTED AND DRIVER */}
        {carLists && carLists?.length > 0 && (
          <div className={cn(styles.carsListWrapper)}>

            <ProviderOutOfServiceWarning />

            {isAnyCarIncluded && <UpgradeMessage />}

            { carLists?.map(({ selectedId, selectedValue }: { selectedId: string, selectedValue: string }, index) => {
              const optionId = buildUniqOptionCarId(selectedId, selectedValue)
              const selectedOption = options?.[optionId]
              if (!selectedOption)
                return null
              return (
                <div className={cn(styles.carSelectedWrapper)} key={optionId}>
                  { service.isOpenJawPackage && selectedOption.openJawFeeAmount > 0 && <OpenJawMessage openJawFeeAmount={selectedOption.openJawFeeAmount} /> }
                  <CarOption isSelected {...selectedOption} />
                  {service.maxNumbersOfCarsToBeBooked > carLists?.length && (carLists?.length === index + 1) && <ExtraCarsView {...service} />}
                </div>
              )
            })}
          </div>
        )}

        {/* First selection made */}
        { isCarBeingSelected && selectingCarOption && (
          <CarOption isSelected {...selectingCarOption} openDriverByDefault={isCarBeingSelected} />
        )}

        {/* Initial state */}
        {!selectingCarOption && carLists && carLists?.length === 0 && <CarsListView />}

        <UpgradeCarsListView
          open={isCarBeingUpgraded}
        />
      </div>

    </CheckoutSection>
  )
}

function ProviderOutOfServiceWarning() {
  const intl = useIntl()
  const { service } = useService(ServiceType.CarRentalFiltering)

  if (!service)
    return null

  const isProviderOutOfService = service?.carProviderIsOutOfService ?? false

  if (!isProviderOutOfService)
    return null

  return (
    <Banner
      color="informative"
      size="M"
      hasIcon
      icon="ArrowUpInCircle"
      text={intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_provider_is_down_banner_text' })}
      supportingText={intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_provider_is_down_banner_supporting_text' })}
    />
  )
}

function UpgradeMessage() {
  const intl = useIntl()
  const { service } = useService(ServiceType.CarRentalFiltering)
  const { data } = useGetAllCarsQuery()

  if (!service)
    return null

  const { options } = service

  const carIncluded = getFirstCarIncluded(Object.values(options || {}))

  const canSelectedAndIncludedCarBeingUpgraded = isAnyCarOptionSelectedAndIncludedAndUpgradeable(Object.values(options || {}), Object.values(service.carLists || {}).map(({ selectedId }) => selectedId ?? ''))
  const filterableCars = data?.cars ?? {}

  const priceToUpgrade = getThePriceDifferenceToUpgradeIncludedCar(Object.values(filterableCars ?? {}), carIncluded)

  if (!canSelectedAndIncludedCarBeingUpgraded || !priceToUpgrade)
    return null

  return (
    <Banner
      color="informative"
      size="M"
      hasIcon
      icon="ArrowUpInCircle"
      text={intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_upgrade_banner_text' }, { price: intl.formatCurrency(priceToUpgrade ?? 0) })}
      supportingText={intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_upgrade_banner_supporting_text' })}
    />
  )
}

function WarningMessages() {
  const intl = useIntl()
  const { service } = useService(ServiceType.CarRentalFiltering)

  if (!service)
    return null

  if (!service.includedCarOutOfStock
    && !service.includedCarPriceChanged) { return null }

  return (
    <div className={cn(styles.warningMessageWrapper)}>
      { service.includedCarOutOfStock && (
        <Banner
          color="warning"
          size="M"
          text={intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_included_car_not_available_title' })}
          supportingText={intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_included_car_not_available_description' })}
        />
      )}
      { service.includedCarPriceChanged && (
        <Banner
          color="warning"
          size="M"
          text={intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_included_car_price_changed_title' })}
          supportingText={intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_included_car_price_changed_description' })}
        />
      )}
    </div>
  )
}

function OpenJawMessage({ openJawFeeAmount }: { openJawFeeAmount: number }) {
  const intl = useIntl()
  return (
    <Banner
      color="informative"
      size="M"
      text={intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_open_jaw_car_title' })}
      supportingText={intl.formatMessage(
        { id: 'checkout_steps_ancillaries_car_rental_open_jaw_car_description' },
        { openJawFeeAmount: intl.formatCurrency(openJawFeeAmount) },
      )}
    />
  )
}
